import { forwardRef, ReactNode, useState } from 'react'
import { IoCheckmark, IoClose } from 'react-icons/io5'

import { ApolloError } from '@apollo/client'
import { ApolloErrorMessage } from '@ui/components/core/ApolloErrorMessage'
import { CloseIcon, EditLineIcon } from '@ui/components/icons'
import { Spinner } from '@ui/components/loaders'
import clsx from 'clsx'

type PanelContainerProps = {
  children: ReactNode
  title: string
  subtitle?: string
  showDescription?: boolean
  description?: string
  updateDescription?: (description: string) => void
  loading?: boolean
  error?: ApolloError
  onClose?: () => void
  panelActionElement?: ReactNode
}

export const PanelContainer = forwardRef<HTMLDivElement, PanelContainerProps>(
  (
    {
      children,
      title,
      subtitle,
      loading,
      error,
      onClose,
      showDescription = false,
      description,
      updateDescription,
      panelActionElement,
    },
    ref
  ) => {
    const [showDescriptionInput, setShowDescriptionInput] = useState(false)
    const [updatedDescription, setUpdatedDescription] = useState(description)

    return (
      <div className="relative flex size-full flex-col" ref={ref}>
        <div className="flex h-12 items-center justify-between border-b border-gray-100 p-4">
          <p className="w-full text-sm font-semibold">{title}</p>
          {onClose && !panelActionElement && (
            <CloseIcon
              onClick={onClose}
              className="size-3 cursor-pointer stroke-gray-300 transition-all duration-300 ease-in-out hover:scale-110 hover:stroke-gray-500"
            />
          )}
          {panelActionElement && !onClose && panelActionElement}
        </div>
        <>
          {subtitle && <p className="px-4 py-2 text-xs text-gray-700">{subtitle}</p>}
          <div className={clsx('flex flex-col space-y-1 px-4 pt-2', { hidden: !showDescription })}>
            <p className="text-xs font-semibold">Description</p>
            <div className="flex flex-row space-x-2">
              <EditLineIcon
                className={clsx('size-4 cursor-pointer transition-all duration-300 ease-in-out hover:stroke-gray-500', {
                  'stroke-gray-300': showDescriptionInput,
                  'stroke-gray-500': !showDescriptionInput,
                })}
                onClick={() => setShowDescriptionInput(!showDescriptionInput)}
              />
              {showDescriptionInput ? (
                <div className="relative w-full">
                  <div className="absolute -top-5 right-0 flex flex-row">
                    <div
                      onClick={() => {
                        setShowDescriptionInput(false)
                        updateDescription?.(updatedDescription || description || '')
                      }}
                      className="flex size-5 cursor-pointer items-center justify-center rounded bg-success-100 transition-all duration-300 ease-in-out hover:bg-success-50">
                      <IoCheckmark className="size-4 stroke-success-500 transition-all duration-300 ease-in-out hover:stroke-success-600" />
                    </div>
                    <div
                      onClick={() => {
                        setShowDescriptionInput(false)
                        setUpdatedDescription(description)
                      }}
                      className="flex size-5 cursor-pointer items-center justify-center rounded bg-negative-100 transition-all duration-300 ease-in-out hover:bg-negative-50">
                      <IoClose className="size-4 text-negative-500 transition-all duration-300 ease-in-out hover:text-negative-600" />
                    </div>
                  </div>
                  <textarea
                    className="h-10 w-full resize-none rounded-md border border-gray-300 bg-white p-1 text-xs font-normal"
                    value={updatedDescription}
                    onChange={(e) => setUpdatedDescription(e.target.value)}
                  />
                </div>
              ) : (
                <p className="text-xs font-normal">{description || 'No description'}</p>
              )}
            </div>
          </div>
        </>
        {error && (
          <div className="px-4 py-2">
            <ApolloErrorMessage error={error} />
          </div>
        )}
        {loading ? (
          <div className="flex h-full items-center justify-center p-4">
            <Spinner />
          </div>
        ) : (
          <div className="size-full">{children}</div>
        )}
      </div>
    )
  }
)
